// Dải vòng đời — các thẻ của một trang là những lát cắt của cùng một mạch.
//
// Vì sao hộp nhiều thẻ cần cái này: đứng trong MỘT thẻ thì không thấy được
// mạch, nên người đọc học thuộc "thẻ này làm gì" mà vẫn không hiểu vì sao một
// bản ghi biến khỏi thẻ mình rồi hiện ra ở thẻ người khác. Dải nối các lát cắt
// lại và tô đậm chặng ứng với thẻ đang mở, để họ biết mình đang đứng ở khúc nào.
//
// Xếp DỌC chứ không xếp ngang: bốn chặng kèm tên thẻ là quá nhiều chữ cho một
// hàng ngang, bóp lại thì tràn ở điện thoại.
//
// Màu lấy từ token semantic của app, KHÔNG gõ hex tay: bản pastel `--color-X`
// contrast chỉ 2.24-3.28 nên chỉ dùng làm NỀN và viền, chữ phải dùng
// `--color-X-text` (4.9-5.71, đạt AA) — xem chú thích đo thật ở `tokens.css`.
// Dùng token cũng để dải đổi theo chủ đề và ăn khớp với badge trên màn thật;
// badge trên màn một màu, hướng dẫn một màu là người đọc phải dịch.

import type { StatusTone } from "./GuideSections";
import type { GuideFlow } from "./multi-tab-guides";

const TONE: Record<StatusTone, { bg: string; text: string; border: string }> = {
  info: { bg: "var(--color-info-bg)", text: "var(--color-info-text)", border: "var(--color-info)" },
  warning: {
    bg: "var(--color-warning-bg)",
    text: "var(--color-warning-text)",
    border: "var(--color-warning)",
  },
  success: {
    bg: "var(--color-success-bg)",
    text: "var(--color-success-text)",
    border: "var(--color-success)",
  },
  danger: {
    bg: "var(--color-danger-bg)",
    text: "var(--color-danger-text)",
    border: "var(--color-danger)",
  },
};

/**
 * Nhãn thẻ để in trên mỗi chặng — nhận từ ngoài để không gõ chuỗi lần hai.
 *
 * Chỉ chứa thẻ người đọc THẬT SỰ mở được, nên khoá nào cũng có thể vắng: chặng
 * nào không còn thẻ nào của họ thì dải bỏ hàng chip đi chứ không in ô trống,
 * và cũng không kể tên thẻ thuộc việc của người khác.
 */
export type NhanThe = Record<string, string | undefined>;

export function GuideFlowStrip({
  flow,
  dangMo,
  nhan,
}: {
  flow: GuideFlow;
  dangMo: string;
  nhan: NhanThe;
}) {
  return (
    <section className="space-y-2">
      <h3 className="font-semibold">{flow.heading}</h3>
      <p className="text-muted">{flow.intro}</p>

      <ol className="space-y-0">
        {flow.stages.map((c, i) => {
          const tone = TONE[c.tone];
          const dangDung = c.the.includes(dangMo);
          // Chỉ kể những thẻ người đọc mở được. Chặng vẫn hiện đủ để họ thấy
          // mạch chạy tới đâu, nhưng không kể tên thẻ của việc người khác — và
          // không in ô trống khi chẳng còn thẻ nào.
          const theDoc = c.the.filter((k) => nhan[k]);
          return (
            <li key={c.ten}>
              {i > 0 && (
                <div className="flex items-center gap-2 py-1 pl-4" aria-hidden>
                  <span className="text-base font-semibold text-muted">{c.nhanh ? "↳" : "↓"}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              <div
                className={`rounded-md border-l-4 p-3 ${dangDung ? "ring-2 ring-offset-1" : ""}`}
                style={{
                  background: tone.bg,
                  borderLeftColor: tone.border,
                  ...(dangDung ? { ["--tw-ring-color" as string]: tone.border } : {}),
                }}
              >
                <p className="text-[13px] font-semibold" style={{ color: tone.text }}>
                  {c.nhanh && <span className="sr-only">rẽ nhánh: </span>}
                  {c.ten}
                  {dangDung && <span className="ml-2 font-normal">— thẻ bạn đang mở nằm ở đây</span>}
                </p>
                <p className="mt-1 leading-relaxed">{c.ke}</p>
                {theDoc.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted">Thẻ:</span>
                    {theDoc.map((k) => (
                      <span
                        key={k}
                        className={`rounded border px-1.5 py-0.5 text-xs ${
                          k === dangMo ? "border-current font-semibold" : "border-border bg-surface"
                        }`}
                      >
                        {nhan[k]}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
